/**
 * Customer-safe public config (`GET /api/config/public`) — currently just the
 * custom-work show/hide toggle.
 *
 * Self-contained module (does its own fetch, not via api.ts) so pages that
 * are unit-tested with `vi.mock("../lib/api")` can still mount it without the
 * mock needing to know about it. Failure is cosmetic and always resolves
 * toward HIDING the flow — mirroring the cloud endpoint's own fallback.
 */

import { useEffect, useState } from "react";

export interface PublicConfig {
  custom_work_enabled: boolean;
}

const DISABLED: PublicConfig = { custom_work_enabled: false };

let cached: Promise<PublicConfig> | null = null;

export function fetchPublicConfig(): Promise<PublicConfig> {
  if (!cached) {
    try {
      cached = fetch("/api/config/public")
        .then((r) => (r.ok ? (r.json() as Promise<PublicConfig>) : DISABLED))
        .then((cfg) => ({
          custom_work_enabled: Boolean(cfg?.custom_work_enabled),
        }))
        .catch(() => DISABLED);
    } catch {
      // fetch itself can throw synchronously (e.g. relative URL outside a
      // browser); treat exactly like a network failure.
      cached = Promise.resolve(DISABLED);
    }
  }
  return cached;
}

/** `null` while loading, then the resolved config. */
export function usePublicConfig(): PublicConfig | null {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  useEffect(() => {
    let active = true;
    void fetchPublicConfig().then((cfg) => {
      if (active) setConfig(cfg);
    });
    return () => {
      active = false;
    };
  }, []);
  return config;
}
